import axios from '../api';

/**
 * Action Plan Service
 * Handles API calls for action plan management
 */

/**
 * Generate and save new action plan from AI
 * @param {string} patientId - Patient ID
 * @param {string} language - Language for the action plan
 * @returns {Promise<Object>} Generated action plan
 */
export const generateAndSaveActionPlan = async (patientId, language = 'english') => {
  try {
    const response = await axios.post(`/patients/${patientId}/action-plans/generate`, {
      language
    });
    return response.data;
  } catch (error) {
    console.error('Error generating and saving action plan:', error);
    throw error;
  }
};

/**
 * Create and save new action plan manually
 * @param {string} patientId - Patient ID
 * @param {Object} actionPlanData - Action plan data
 * @returns {Promise<Object>} Created action plan
 */
export const createActionPlan = async (patientId, actionPlanData) => {
  try {
    const response = await axios.post(`/patients/${patientId}/action-plans/save-existing`, actionPlanData);
    return response.data;
  } catch (error) {
    console.error('Error creating action plan:', error);
    throw error;
  }
};

/**
 * Get all action plans for a patient
 * @param {string} patientId - Patient ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Action plans list
 */
export const getPatientActionPlans = async (patientId, params = {}) => {
  try {
    const response = await axios.get(`/patients/${patientId}/action-plans`, {
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching patient action plans:', error);
    throw error;
  }
};

/**
 * Get action plan by ID
 * @param {string} actionPlanId - Action plan ID
 * @param {boolean} includeHistory - Include modification history
 * @returns {Promise<Object>} Action plan details
 */
export const getActionPlanById = async (actionPlanId, includeHistory = false) => {
  try {
    const response = await axios.get(`/action-plans/${actionPlanId}`, {
      params: { includeHistory }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching action plan:', error);
    throw error;
  }
};

/**
 * Update action plan section
 * @param {string} actionPlanId - Action plan ID
 * @param {string} sectionId - Section ID
 * @param {string} content - New content
 * @param {string} changeDescription - Description of changes
 * @returns {Promise<Object>} Updated action plan
 */
export const updateActionPlanSection = async (actionPlanId, sectionId, content, changeDescription = '') => {
  try {
    const response = await axios.put(`/action-plans/${actionPlanId}/sections/${sectionId}`, {
      content,
      changeDescription
    });
    return response.data;
  } catch (error) {
    console.error('Error updating action plan section:', error);
    throw error;
  }
};

/**
 * Update action plan status
 * @param {string} actionPlanId - Action plan ID
 * @param {string} status - New status
 * @param {string} reason - Reason for status change
 * @returns {Promise<Object>} Updated action plan
 */
export const updateActionPlanStatus = async (actionPlanId, status, reason = '') => {
  try {
    const response = await axios.put(`/action-plans/${actionPlanId}/status`, {
      status,
      reason
    });
    return response.data;
  } catch (error) {
    console.error('Error updating action plan status:', error);
    throw error;
  }
};

/**
 * Duplicate action plan
 * @param {string} actionPlanId - Action plan ID to duplicate
 * @param {Object} options - Duplication options
 * @returns {Promise<Object>} Duplicated action plan
 */
export const duplicateActionPlan = async (actionPlanId, options = {}) => {
  try {
    const response = await axios.post(`/action-plans/${actionPlanId}/duplicate`, options);
    return response.data;
  } catch (error) {
    console.error('Error duplicating action plan:', error);
    throw error;
  }
};

/**
 * Delete action plan (soft delete)
 * @param {string} actionPlanId - Action plan ID
 * @param {Object} options - Deletion options
 * @returns {Promise<Object>} Deletion result
 */
export const deleteActionPlan = async (actionPlanId, options = {}) => {
  try {
    const response = await axios.delete(`/action-plans/${actionPlanId}`, {
      data: options
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting action plan:', error);
    throw error;
  }
};

/**
 * Get action plan statistics for patient
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Action plan statistics
 */
export const getActionPlanStats = async (patientId) => {
  try {
    const response = await axios.get(`/patients/${patientId}/action-plans/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching action plan stats:', error);
    throw error;
  }
};

/**
 * Export action plan to PDF
 * @param {string} actionPlanId - Action plan ID
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} PDF blob
 */
export const exportActionPlanToPDF = async (actionPlanId, options = {}) => {
  try {
    const response = await axios.get(`/action-plans/${actionPlanId}/export/pdf`, {
      params: options,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting action plan to PDF:', error);
    throw error;
  }
};

/**
 * Search action plans
 * @param {string} patientId - Patient ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Search results
 */
export const searchActionPlans = async (patientId, query, options = {}) => {
  try {
    const response = await axios.get(`/patients/${patientId}/action-plans`, {
      params: {
        search: query,
        ...options
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching action plans:', error);
    throw error;
  }
};

/**
 * Get action plan modification history
 * @param {string} actionPlanId - Action plan ID
 * @param {string} sectionId - Section ID (optional)
 * @returns {Promise<Object>} Modification history
 */
export const getActionPlanHistory = async (actionPlanId, sectionId = null) => {
  try {
    const response = await axios.get(`/action-plans/${actionPlanId}/history`, {
      params: sectionId ? { sectionId } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching action plan history:', error);
    throw error;
  }
};

/**
 * Restore action plan section from history
 * @param {string} actionPlanId - Action plan ID
 * @param {string} sectionId - Section ID
 * @param {string} historyId - History entry ID
 * @returns {Promise<Object>} Restored action plan
 */
export const restoreActionPlanSection = async (actionPlanId, sectionId, historyId) => {
  try {
    const response = await axios.post(`/action-plans/${actionPlanId}/sections/${sectionId}/restore`, {
      historyId
    });
    return response.data;
  } catch (error) {
    console.error('Error restoring action plan section:', error);
    throw error;
  }
};

/**
 * Assign users to action plan
 * @param {string} actionPlanId - Action plan ID
 * @param {Array} assignments - User assignments
 * @returns {Promise<Object>} Updated action plan
 */
export const assignUsersToActionPlan = async (actionPlanId, assignments) => {
  try {
    const response = await axios.put(`/action-plans/${actionPlanId}/assignments`, {
      assignments
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning users to action plan:', error);
    throw error;
  }
};

/**
 * Add tags to action plan
 * @param {string} actionPlanId - Action plan ID
 * @param {Array} tags - Tags to add
 * @returns {Promise<Object>} Updated action plan
 */
export const addActionPlanTags = async (actionPlanId, tags) => {
  try {
    const response = await axios.put(`/action-plans/${actionPlanId}/tags`, {
      tags
    });
    return response.data;
  } catch (error) {
    console.error('Error adding tags to action plan:', error);
    throw error;
  }
};

/**
 * Get action plan audit log
 * @param {string} actionPlanId - Action plan ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Audit log entries
 */
export const getActionPlanAuditLog = async (actionPlanId, options = {}) => {
  try {
    const response = await axios.get(`/action-plans/${actionPlanId}/audit`, {
      params: options
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching action plan audit log:', error);
    throw error;
  }
};
